import apiV2 from '../api/v2';
import { toDmError } from './dmErrors';

/** Excluded teeth per DM notification reference, as ISO (FDI) tooth numbers. */
export type ExcludedTeethMap = Record<string, string[]>;

export interface SetNextScanParams {
    /** yyyy-mm-dd, read in the patient's timezone by DM. */
    dueDate: string;
    /** Optional message DM sends the patient. */
    comment?: string;
}

export async function setNextScanDate(patientId: string, params: SetNextScanParams): Promise<void> {
    try {
        await apiV2.patch(`/patients/${patientId}/dm/monitoring/next-scan`, params);
    } catch (err) {
        throw toDmError(err);
    }
}

export interface RequestComfortScanParams {
    /** Optional message DM sends the patient. */
    comment?: string;
}

export interface ComfortScanResult {
    scanId: string | number;
    dueDate: string | null;
    createdAt: string | null;
}

export async function requestComfortScan(
    patientId: string,
    params: RequestComfortScanParams
): Promise<ComfortScanResult> {
    try {
        const response = await apiV2.post<{ success: boolean; data: ComfortScanResult }>(
            `/patients/${patientId}/dm/scans/comfort`,
            params
        );
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export interface ForceNextAlignerChangeResult {
    forceNextAlignerChange: boolean;
}

const FORCE_NEXT_ALIGNER_PATH = (patientId: string) =>
    `/patients/${patientId}/dm/aligners/force-next`;

/** Rejects with ApiConflictError when a change is already pending. */
export async function forceNextAlignerChange(
    patientId: string
): Promise<ForceNextAlignerChangeResult> {
    try {
        const response = await apiV2.post<{
            success: boolean;
            data: ForceNextAlignerChangeResult;
        }>(FORCE_NEXT_ALIGNER_PATH(patientId));
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

/** Rejects with ApiConflictError when no change is pending. */
export async function cancelForcedAlignerChange(
    patientId: string
): Promise<ForceNextAlignerChangeResult> {
    try {
        const response = await apiV2.delete<{
            success: boolean;
            data: ForceNextAlignerChangeResult;
        }>(FORCE_NEXT_ALIGNER_PATH(patientId));
        return response.data.data;
    } catch (err) {
        throw toDmError(err);
    }
}

export async function getExcludedTeeth(patientId: string): Promise<ExcludedTeethMap> {
    try {
        const response = await apiV2.get<{ success: boolean; data: ExcludedTeethMap }>(
            `/patients/${patientId}/dm/excluded-teeth`
        );
        return response.data.data ?? {};
    } catch (err) {
        throw toDmError(err);
    }
}

export async function updateExcludedTeeth(
    patientId: string,
    changes: ExcludedTeethMap
): Promise<ExcludedTeethMap> {
    try {
        const response = await apiV2.patch<{ success: boolean; data: ExcludedTeethMap }>(
            `/patients/${patientId}/dm/excluded-teeth`,
            { changes }
        );
        return response.data.data ?? {};
    } catch (err) {
        throw toDmError(err);
    }
}
